// BluetoothTest.js
import React, { useEffect, useMemo, useState, useContext } from "react";
import { useLocation } from "react-router-dom";
import { RawBTPrinter } from "../utils/rawbtPrinter";
import ReceiptPreview from "./ReceiptPreview";
import { SAVED_URL } from "../components/api/apiurls";
import { AuthContext } from "../components/ContextApi/ContextApi";
import Navbar from "../components/shared/Navbar";

// Utility: get latest entry per account
const getLatestEntries = (data = []) => {
  const latestMap = {};
  data.forEach((entry) => {
    if (!entry) return;
    const accNo = String(entry.AccountNo);
    if (!latestMap[accNo] || entry.id > latestMap[accNo].id) {
      latestMap[accNo] = entry;
    }
  });
  return Object.values(latestMap);
};

export default function BluetoothTest() {
  const { state } = useLocation(); // state from navigation contains all report data
  const [apiData, setApiData] = useState([]);
  const [loading, setLoading] = useState(true);

  // ✅ useContext for bank + agentUserName
  const { firstName, lastName, bank, userName: agentUserName, userMobile } = useContext(AuthContext);

  // helpers
  const padLine = (label, value, lineWidth = 32) => {
    const labelStr = label || "";
    const valueStr = value || "";
    let spaces = lineWidth - labelStr.length - valueStr.length;
    if (spaces < 1) spaces = 1;
    return labelStr + " ".repeat(spaces) + valueStr;
  };

  const formatAmount = (val) => {
    if (val === undefined || val === null || val === "") return "0.00";
    let num = parseFloat(val);
    if (isNaN(num)) return "0.00";
    return num.toFixed(2);
  };

  // Fetch API once component mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${SAVED_URL}${agentUserName}/`);
        const data = await res.json();
        setApiData(data);
      } catch (err) {
        console.error("API fetch failed", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [agentUserName]);

  // Use data from navigation state (all report data) or fallback to API data
  const reportData = useMemo(() => {
    return state?.data && state.data.length > 0 ? state.data : apiData;
  }, [state?.data, apiData]);

  // Build preview text for all reports
  const previewText = useMemo(() => {
    if (!reportData.length) return "No report data available.";
    
    let lines = [];
    lines.push("  **** DAILY REPORT ****");
    lines.push(` ${bank || ""}`);
    lines.push(`Date: ${new Date().toLocaleDateString()}`);
    lines.push(`Agent: ${firstName + " " + lastName || ""}`);
    lines.push("");
    lines.push("=".repeat(32));
    
    // Summary header
    lines.push(padLine("ACCOUNT NO", "RECEIVED"));
    lines.push("-".repeat(32));
    
    // Individual entries
    let totalReceived = 0;
    reportData.forEach((entry, index) => {
      const received = parseFloat(entry.Recieved) || 0;
      totalReceived += received;
      
      lines.push(padLine(
        entry.AccountNo || "----", 
        formatAmount(entry.Recieved)
      ));
      
      // Add separator every 5 entries for readability
      if ((index + 1) % 5 === 0 && index !== reportData.length - 1) {
        lines.push("-".repeat(32));
      }
    });
    
    lines.push("=".repeat(32));
    lines.push(padLine("TOTAL RECEIVED", formatAmount(totalReceived)));
    lines.push("");
    lines.push("  END OF REPORT");
    lines.push(`       ${firstName + " " + lastName || ""}`);
    lines.push(`  ${userMobile || ""}`);
    
    return lines.join("\n");
  }, [reportData, bank, firstName, lastName, userMobile]);

  // Print all reports in a summary format
  const handlePrintReport = () => {
    if (!reportData.length) {
      alert("No report data found!");
      return;
    }

    let commands = "";
    commands += String.fromCharCode(27) + "@"; // Initialize printer

    // ✅ Center align for header
    commands += String.fromCharCode(27) + "a" + String.fromCharCode(1);

    // ✅ Set Double Width + Bold for the title
    commands += String.fromCharCode(27) + "!" + String.fromCharCode(32);
    commands += String.fromCharCode(27) + "E" + String.fromCharCode(1);
    commands += "*** DAILY REPORT ***\n";
    commands += String.fromCharCode(27) + "E" + String.fromCharCode(0);

    // ✅ Reset to normal font size
    commands += String.fromCharCode(27) + "!" + String.fromCharCode(0);

    // ✅ Bank name
    commands += String.fromCharCode(27) + "E" + String.fromCharCode(1);
    commands += `${bank || ""}\n`;
    commands += String.fromCharCode(27) + "E" + String.fromCharCode(0);

    // ✅ Report details
    commands += `Date: ${new Date().toLocaleDateString()}\n`;
    commands += `Agent: ${firstName + " " + lastName || ""}\n`;
    commands += "\n";

    // ✅ Left align for data
    commands += String.fromCharCode(27) + "a" + String.fromCharCode(0);

    // ✅ Separator line
    commands += "=".repeat(32) + "\n";

    // ✅ Column headers
    commands += String.fromCharCode(27) + "E" + String.fromCharCode(1);
    commands += padLine("ACCOUNT NO", "RECEIVED") + "\n";
    commands += String.fromCharCode(27) + "E" + String.fromCharCode(0);
    commands += "-".repeat(32) + "\n";

    // ✅ Individual entries
    let totalReceived = 0;
    reportData.forEach((entry, index) => {
      const received = parseFloat(entry.Recieved) || 0;
      totalReceived += received;
      
      commands += padLine(
        entry.AccountNo || "----", 
        formatAmount(entry.Recieved)
      ) + "\n";
      
      // Add separator every 5 entries for readability
      if ((index + 1) % 5 === 0 && index !== reportData.length - 1) {
        commands += "-".repeat(32) + "\n";
      }
    });

    // ✅ Footer with total
    commands += "=".repeat(32) + "\n";
    commands += String.fromCharCode(27) + "E" + String.fromCharCode(1);
    commands += padLine("TOTAL RECEIVED", formatAmount(totalReceived)) + "\n";
    commands += String.fromCharCode(27) + "E" + String.fromCharCode(0);
    commands += "\n";

    // ✅ Center align for footer
    commands += String.fromCharCode(27) + "a" + String.fromCharCode(1);
    commands += "END OF REPORT\n";
    commands += `${firstName + " " + lastName || ""}\n`;
    commands += `${userMobile || ""}\n`;

    // ✅ Feed paper for cutting
    commands += "\n\n\n";

    // ✅ Send to printer
    RawBTPrinter.printEscPos(commands);
  };

  // Print individual receipts (preserved for backward compatibility)
  const handlePrintReceipt = (entry) => {
    if (!entry) {
      alert("No receipt data found!");
      return;
    }

    let commands = "";
    commands += String.fromCharCode(27) + "@";

    commands += String.fromCharCode(27) + "a" + String.fromCharCode(1);
    commands += String.fromCharCode(27) + "!" + String.fromCharCode(32);
    commands += String.fromCharCode(27) + "E" + String.fromCharCode(1);
    commands += "*** RECEIPT ***\n";
    commands += String.fromCharCode(27) + "E" + String.fromCharCode(0);
    commands += String.fromCharCode(27) + "!" + String.fromCharCode(0);

    commands += String.fromCharCode(27) + "E" + String.fromCharCode(1);
    commands += `${bank || ""}\n`;
    commands += String.fromCharCode(27) + "E" + String.fromCharCode(0);

    commands += String.fromCharCode(27) + "a" + String.fromCharCode(0);
    commands += `Rec No: ${entry.id || "----"}  Date: ${entry.Today || new Date().toLocaleDateString()}\n`;
    commands += `NAME : ${entry.Name || ""}\n`;
    commands += `ACCOUNT NO : ${entry.AccountNo || ""}\n`;
    commands += `ACCOUNT TYPE: ${entry.AccountType || ""}\n\n`;

    commands += String.fromCharCode(27) + "E" + String.fromCharCode(1);
    commands += padLine("OPENING BALANCE", formatAmount(entry.PreviousBalance)) + "\n";
    commands += padLine("RECEIVED", formatAmount(entry.Recieved)) + "\n";
    commands += padLine("BALANCE", formatAmount(entry.PresentBalance)) + "\n";
    commands += String.fromCharCode(27) + "E" + String.fromCharCode(0);

    commands += "\n";
    commands += String.fromCharCode(27) + "a" + String.fromCharCode(1);
    commands += String.fromCharCode(27) + "!" + String.fromCharCode(32);
    commands += "THANK YOU\n";
    commands += String.fromCharCode(27) + "!" + String.fromCharCode(0);
    commands += `${firstName + " " + lastName || ""}\n`;
    commands += `${userMobile || ""}\n`;
    commands += "\n\n\n";

    RawBTPrinter.printEscPos(commands);
  };

return (
  <div>
    <Navbar />

    {/* MAIN WRAPPER WITH CORRECT TOP SPACING */}
    <div
      style={{
        padding: "10px 20px 0 20px",
        textAlign: "center",
        marginTop: "70px",  
      }}
    >
      {loading ? (
        <p>Loading...</p>
      ) : reportData.length > 0 ? (
        <>

          {/* FIXED TOP PRINT BUTTON SPACING */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: "10px", 
              marginRight: "2px",
            }}
          >
            <button
              onClick={handlePrintReport}
              style={{
                padding: "10px 20px",
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "4px",
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              🖨 Print Full Report
            </button>
          </div>

          {/* REPORT PREVIEW */}
          <div style={{ marginTop: "-35px" }}>
  <ReceiptPreview text={previewText} />
</div>

          {/* FIXED BOTTOM BUTTON SPACING */}
          <div
            style={{
              marginTop: "20px",       // FIXED: Not too high
              marginBottom: "20px",
              display: "flex",
              justifyContent: "center",
              gap: "10px",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={handlePrintReport}
              style={{
                padding: "10px 20px",
                backgroundColor: "#007bff",
                color: "white",
                border: "none",
                borderRadius: "4px",
              }}
            >
              🖨 Print Full Report
            </button>

            {state?.AccountNo && (
              <button
                onClick={() =>
                  handlePrintReceipt(
                    reportData.find(
                      (item) =>
                        String(item.AccountNo) === String(state.AccountNo)
                    )
                  )
                }
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#28a745",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                }}
              >
                🖨 Print Single Receipt
              </button>
            )}
          </div>

          {/* SUMMARY CARD */}
          <div
            style={{
              marginTop: 10,
              textAlign: "left",
              maxWidth: "500px",
              margin: "20px auto",
              padding: "15px",
              border: "1px solid #ddd",
              borderRadius: "4px",
            }}
          >
            <h3>Report Summary</h3>
            <p>
              <strong>Total Entries:</strong> {reportData.length}
            </p>
            <p>
              <strong>Total Received:</strong>{" "}
              {formatAmount(
                reportData.reduce(
                  (sum, item) => sum + (parseFloat(item.Recieved) || 0),
                  0
                )
              )}
            </p>
            <p>
              <strong>Date:</strong> {new Date().toLocaleDateString()}
            </p>
          </div>
        </>
      ) : (
        <p>No report data found</p>
      )}
    </div>
  </div>
);
}